// Help Content for EQUIPR Horizon
// This file contains all article content for the Help & Support system

export const HELP_CONTENT = {
  // ==========================================
  // GETTING STARTED
  // ==========================================
  
  'gs-1': {
    title: 'Welcome to EQUIPR Horizon',
    category: 'Getting Started',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `EQUIPR Horizon is a comprehensive equipment and personnel planning platform designed for resource-intensive industries. It helps you manage your equipment fleet, workforce, and project allocations in one centralized system.`
      },
      {
        heading: 'Key Features',
        content: `
• **Equipment Management** - Track your entire equipment fleet, maintenance schedules, and certifications
• **Personnel Management** - Manage workforce records, FIFO rotations, skills, and compliance
• **Project Planning** - Create projects, set budgets, and allocate resources
• **Visual Planners** - Gantt-style views for equipment and personnel scheduling
• **Compliance Tracking** - Automated alerts for expiring certifications and documents
• **Multi-Tenant Support** - Secure data isolation for different organizations`
      },
      {
        heading: 'Getting Started Checklist',
        content: `
1. **Set up your data categories** - Go to Data Management to configure equipment types, personnel roles, and other lookup data
2. **Add your equipment** - Enter your equipment fleet with details and maintenance schedules
3. **Add your personnel** - Create workforce records with skills, certifications, and rotation patterns
4. **Create projects** - Set up your active and upcoming projects
5. **Start planning** - Use the visual planners to allocate resources to projects`
      }
    ],
    tips: [
      'Start with Data Management to set up categories before adding equipment or personnel',
      'Use the Dashboard to get a quick overview of your operations'
    ],
    relatedArticles: ['gs-2', 'gs-3', 'gs-4']
  },

  'gs-2': {
    title: 'Understanding the Dashboard',
    category: 'Getting Started',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `The Dashboard is your command center, providing at-a-glance visibility into your operations. It displays key metrics, alerts, and quick access to important information.`
      },
      {
        heading: 'Dashboard Widgets',
        content: `
**Summary Cards**
• Equipment count and status breakdown
• Personnel count and availability
• Active projects and their status
• Upcoming alerts count

**Alerts Panel**
• Expiring certifications and compliance items
• Maintenance due dates
• Visa and medical expiries
• Priority-coded (High/Medium/Low)

**Equipment Utilisation**
• Shows equipment allocation rates
• Identifies underutilised assets
• Collapsible for space management

**Personnel Utilisation**
• Workforce allocation overview
• Available vs allocated personnel
• Skill availability insights`
      },
      {
        heading: 'Using the Dashboard Effectively',
        content: `
1. **Check alerts daily** - Review the Alerts panel each morning to stay ahead of compliance issues
2. **Monitor utilisation** - Low utilisation may indicate scheduling opportunities
3. **Quick navigation** - Click on any summary card to go directly to that section
4. **Refresh data** - Dashboard updates automatically but you can refresh the page for latest data`
      }
    ],
    tips: [
      'Collapse utilisation widgets to save space when not needed',
      'High-priority alerts appear in red - address these first',
      'Click "View all on Dashboard" from notifications to see complete alert list'
    ],
    relatedArticles: ['gs-1', 'rp-1', 'rp-3']
  },

  'gs-3': {
    title: 'Navigation & Menu Overview',
    category: 'Getting Started',
    readTime: '2 min',
    sections: [
      {
        heading: 'Main Navigation',
        content: `The left sidebar provides access to all main sections of EQUIPR Horizon. Click the hamburger menu (☰) to expand or collapse the navigation.`
      },
      {
        heading: 'Menu Sections',
        content: `
**Main Areas**
• **Dashboard** - Overview and alerts
• **Equipment** - Equipment database
• **Personnel** - Workforce database
• **Projects** - Project management

**Planning**
• **Equipment Planner** - Visual equipment scheduling
• **Personnel Planner** - Visual workforce scheduling

**Administration**
• **Users** - User management (admin only)
• **Data Management** - Categories and lookup data
• **Reports** - Analytics and reporting`
      },
      {
        heading: 'Top Navigation Bar',
        content: `
• **Search** - Global search across equipment, personnel, and projects
• **Help** - Access help articles and support
• **Notifications** - View alerts and system notifications
• **User Menu** - Profile settings and logout`
      }
    ],
    tips: [
      'Use keyboard shortcut Ctrl+K (or Cmd+K on Mac) to quickly access search',
      'The notification badge shows the number of active alerts'
    ],
    relatedArticles: ['gs-1', 'gs-4']
  },

  'gs-4': {
    title: 'User Roles & Permissions',
    category: 'Getting Started',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `EQUIPR Horizon uses role-based access control to manage what users can see and do. Your permissions depend on your assigned role.`
      },
      {
        heading: 'Available Roles',
        content: `
**Admin**
• Full access to all features
• Can manage users and permissions
• Can access Data Management
• Can view all reports

**Manager**
• Can create and edit equipment, personnel, and projects
• Can use planners and make allocations
• Can view reports
• Cannot manage users or system settings

**Viewer**
• Read-only access to all data
• Can view planners but not make changes
• Can view reports
• Cannot create or edit records`
      },
      {
        heading: 'Multi-Tenant Access',
        content: `
EQUIPR Horizon supports multiple organizations (tenants). Each user belongs to a specific tenant and can only see data for their organization.

• **Client ID** - Your organization identifier (entered at login)
• **Data Isolation** - All data is strictly separated between tenants
• **User Management** - Admins can only manage users within their tenant`
      }
    ],
    tips: [
      'Contact your administrator if you need different permissions',
      'The role is displayed in your user profile dropdown'
    ],
    relatedArticles: ['gs-3', 'ad-1']
  },

  // ==========================================
  // EQUIPMENT MANAGEMENT
  // ==========================================

  'eq-1': {
    title: 'Adding New Equipment',
    category: 'Equipment Management',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `Add equipment to build your asset database. Each piece of equipment can be tracked, allocated to projects, and monitored for maintenance and compliance.`
      },
      {
        heading: 'Step-by-Step Guide',
        content: `
1. Navigate to **Equipment** from the main menu
2. Click the **Add Equipment** button (top right)
3. Fill in the required fields:
   • **Name** - Descriptive name for the equipment
   • **Type** - Select from configured equipment types
   • **Status** - Current operational status
4. Add optional details:
   • Serial number
   • Manufacturer and model
   • Purchase date and value
   • Location
5. Click **Create** to save`
      },
      {
        heading: 'Equipment Status Options',
        content: `
• **Available** - Ready for allocation
• **In Use** - Currently allocated to a project
• **Maintenance** - Undergoing scheduled maintenance
• **Repair** - Out of service for repairs
• **Retired** - No longer in active use`
      }
    ],
    tips: [
      'Set up equipment types in Data Management before adding equipment',
      'Use consistent naming conventions for easier searching',
      'Include serial numbers for warranty and service tracking'
    ],
    relatedArticles: ['eq-2', 'eq-3', 'ad-2']
  },

  'eq-2': {
    title: 'Equipment Categories & Types',
    category: 'Equipment Management',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `Equipment types help organize your fleet and enable filtering and reporting. Types are configured in Data Management and selected when adding equipment.`
      },
      {
        heading: 'Managing Equipment Types',
        content: `
1. Go to **Data Management** from the menu
2. Select **Equipment Type** from the category dropdown
3. View existing types or click **Add Category** to create new ones
4. For each type, you can set:
   • **Name** - The type name (e.g., "Crane", "Forklift")
   • **Description** - Additional details
   • **Active** - Whether the type is available for selection`
      },
      {
        heading: 'Best Practices',
        content: `
• **Be specific** - "Mobile Crane" is better than just "Crane"
• **Standardize naming** - Use consistent terminology across your organization
• **Review regularly** - Deactivate types no longer in use
• **Consider reporting** - Types are used for filtering and analytics`
      }
    ],
    tips: [
      'Equipment types cannot be deleted if equipment is assigned to them',
      'Create types before bulk-importing equipment data'
    ],
    relatedArticles: ['eq-1', 'ad-2']
  },

  'eq-3': {
    title: 'Tracking Maintenance Schedules',
    category: 'Equipment Management',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `Maintenance tracking helps you schedule and monitor equipment servicing, ensuring assets remain operational and compliant.`
      },
      {
        heading: 'Setting Up Maintenance',
        content: `
1. Open the equipment record (click on equipment row or Edit)
2. Navigate to the **Maintenance** tab
3. Add maintenance records:
   • **Type** - Service type (e.g., "Annual Inspection", "Oil Change")
   • **Last Service Date** - When maintenance was last performed
   • **Next Due Date** - When next service is required
   • **Provider** - Service company or technician
   • **Notes** - Additional details or findings`
      },
      {
        heading: 'Maintenance Alerts',
        content: `
The system automatically generates alerts for upcoming maintenance:
• **30 days before** - Early warning notification
• **7 days before** - Urgent reminder
• **Overdue** - High-priority alert

Alerts appear on the Dashboard and in the Notifications panel.`
      },
      {
        heading: 'Maintenance on Gantt',
        content: `
The Equipment Planner shows maintenance periods as overlay bars on the timeline. This helps planners avoid allocating equipment during scheduled maintenance windows.`
      }
    ],
    tips: [
      'Set up maintenance schedules when adding new equipment',
      'Use the Dashboard alerts to stay ahead of service dates',
      'Document all maintenance for compliance and warranty purposes'
    ],
    relatedArticles: ['eq-4', 'pl-1', 'rp-3']
  },

  'eq-4': {
    title: 'Equipment Compliance & Certifications',
    category: 'Equipment Management',
    readTime: '6 min',
    sections: [
      {
        heading: 'Overview',
        content: `Track certifications, inspections, and compliance documents for your equipment. The system alerts you before expiry to maintain operational compliance.`
      },
      {
        heading: 'Adding Certifications',
        content: `
1. Open the equipment record
2. Navigate to the **Certifications** tab
3. Click **Add Certification**
4. Enter details:
   • **Type** - Certification type (e.g., "Load Test", "Safety Inspection")
   • **Certificate Number** - Reference number
   • **Issuer** - Certifying body or inspector
   • **Issue Date** - When certification was granted
   • **Expiry Date** - When certification expires
   • **Status** - Active, Expired, or Pending
5. Click **Save** to add the certification`
      },
      {
        heading: 'Compliance Monitoring',
        content: `
The system monitors all certifications and generates alerts:
• **90 days before expiry** - Planning notice
• **30 days before expiry** - Action required
• **Expired** - High-priority, equipment may not be deployable

View all compliance alerts on the Dashboard or filter equipment by compliance status.`
      }
    ],
    tips: [
      'Upload or reference certification documents for audit purposes',
      'Set up certification types in Data Management for consistency',
      'Review compliance status before allocating equipment to projects'
    ],
    relatedArticles: ['eq-3', 'rp-3', 'ad-2']
  },

  // ==========================================
  // PERSONNEL MANAGEMENT
  // ==========================================

  'pe-1': {
    title: 'Adding Personnel Records',
    category: 'Personnel Management',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `Build your workforce database by adding personnel records. Each record contains personal details, skills, certifications, and compliance information.`
      },
      {
        heading: 'Step-by-Step Guide',
        content: `
1. Navigate to **Personnel** from the main menu
2. Click the **Add Personnel** button
3. Fill in the **General** tab:
   • **Name** - Full name
   • **Role** - Select from configured roles
   • **Skills** - Comma-separated list of skills
   • **Nationality** - Select from dropdown (multi-select)
   • **Languages** - Select from dropdown (multi-select)
4. Set visa and rotation details:
   • **Visa Type** - Work visa category
   • **Visa Expiry** - When visa expires
   • **Rotation Roster** - FIFO pattern (e.g., 2:2)
   • **Next Rotation Start** - When next swing begins
5. Click **Create** to save`
      },
      {
        heading: 'Additional Tabs',
        content: `
• **FIFO** - Manage fly-in/fly-out dates
• **Certifications** - Trade and safety certifications
• **Medical** - Health assessments and clearances
• **Inductions** - Site induction records`
      }
    ],
    tips: [
      'Set up personnel roles in Data Management first',
      'Complete all compliance tabs for accurate alerting',
      'Current Allocation is auto-populated from project assignments'
    ],
    relatedArticles: ['pe-2', 'pe-3', 'pe-4', 'ad-2']
  },

  'pe-2': {
    title: 'FIFO & Rotation Schedules',
    category: 'Personnel Management',
    readTime: '7 min',
    sections: [
      {
        heading: 'Overview',
        content: `Fly-In Fly-Out (FIFO) management tracks when workers travel to and from site, including complete flight details. Rotation schedules define the on/off patterns for roster planning.`
      },
      {
        heading: 'Setting Up Rotations',
        content: `
**Rotation Patterns**
Common patterns include:
• **2:2** - 2 weeks on, 2 weeks off
• **3:1** - 3 weeks on, 1 week off
• **4:3** - 4 weeks on, 3 weeks off
• **Even Time** - Equal on and off periods

**Configuration**
1. Go to **Data Management**
2. Select **Rotation** category
3. Add rotation patterns with:
   • Name (e.g., "2:2 (2 weeks on / 2 weeks off)")
   • Days On
   • Days Off`
      },
      {
        heading: 'Managing FIFO Records',
        content: `
1. Edit the personnel record
2. Go to the **FIFO** tab
3. Click **Add FIFO** to create a new travel record
4. Set the **Status**:
   • **Planned** - Initial booking state
   • **Confirmed** - Flights booked and confirmed
   • **Completed** - Travel finished
   • **Cancelled** - Travel cancelled

**Fly In Details (To Site)**
• **Date** - When arriving at site
• **Time** - Departure time
• **Flight #** - Flight number (e.g., QF1234)
• **From** - Departure airport (e.g., PER - Perth)
• **To** - Arrival airport (e.g., PHE - Port Hedland)

**Fly Out Details (From Site)**
• **Date** - When leaving site
• **Time** - Departure time
• **Flight #** - Return flight number
• **From** - Site airport
• **To** - Home airport

**Additional Fields**
• **Project** - Which project the travel is for
• **Site Location** - Destination site name
• **Notes** - Any additional information

5. Click **Add** to save the FIFO record
6. Click **Update** to save all changes to the personnel record`
      },
      {
        heading: 'Available Airports',
        content: `
The system includes 40+ preconfigured airports commonly used for FIFO operations:

**Australian Airports**
• PER (Perth), PHE (Port Hedland), KTA (Karratha)
• BME (Broome), NTL (Newman), LEA (Learmonth)
• DRW (Darwin), CNS (Cairns), BNE (Brisbane)
• SYD (Sydney), MEL (Melbourne), ADL (Adelaide)
• KGI (Kalgoorlie), GET (Geraldton), and more

**International Airports**
• SIN (Singapore), KUL (Kuala Lumpur), JKT (Jakarta)
• MNL (Manila), BKK (Bangkok), DXB (Dubai)
• AKL (Auckland), POM (Port Moresby), and more`
      },
      {
        heading: 'FIFO Status Workflow',
        content: `
Track each FIFO record through its lifecycle:

1. **Planned** (Blue badge)
   • Initial state when travel is scheduled
   • Flight details may be tentative

2. **Confirmed** (Green badge)
   • Flights booked and tickets issued
   • Ready for travel

3. **Completed** (Gray badge)
   • Worker has completed the swing
   • Historical record

4. **Cancelled** (Red badge)
   • Travel was cancelled
   • Keeps record for reporting`
      },
      {
        heading: 'Gantt View Integration',
        content: `
The Personnel Gantt View displays FIFO and rotation information:

**Rotation Segments**
• **Blue bars** - Rostered On periods (at work)
• **Gray bars** - R&R periods (rest & recreation)
• Segments calculated from Next Rotation Start date

**Flight Markers**
• **Green plane icon** - Fly In (arriving at site)
• **Blue plane icon** - Fly Out (leaving site)
• Icons positioned at exact flight dates on timeline
• Hover over icons to see flight details

**Compliance Markers**
• Orange, red, and purple markers for expiring documents
• Appear on allocation bars when expiry falls within allocation period`
      }
    ],
    tips: [
      'Set Next Rotation Start date to correctly align roster segments on the Gantt',
      'Use Confirmed status once flights are booked to distinguish from planned travel',
      'Flight markers appear on the Gantt View - hover for flight number and route details',
      'Keep FIFO records updated to generate accurate flight manifests',
      'The FIFO tab shows records sorted by Fly In date for easy planning'
    ],
    relatedArticles: ['pe-1', 'pe-5', 'pl-2']
  },

  'pe-3': {
    title: 'Certifications & Compliance',
    category: 'Personnel Management',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `Track trade certifications, licenses, and competencies for your workforce. The system monitors expiry dates and alerts you to maintain compliance.`
      },
      {
        heading: 'Adding Certifications',
        content: `
1. Edit the personnel record
2. Go to the **Certifications** tab
3. Click **Add Certification**
4. Enter:
   • **Type** - Category (e.g., "Trade License", "Safety")
   • **Name** - Specific certification name
   • **Number** - Certificate/license number
   • **Issuer** - Issuing authority
   • **Issue Date** - When granted
   • **Expiry Date** - When it expires
   • **Status** - Active, Expired, Pending
5. Click **Add** then **Update**`
      },
      {
        heading: 'Compliance Alerts',
        content: `
Personnel with expiring certifications appear in:
• **Dashboard Alerts** - Priority-coded notifications
• **Personnel List** - Compliance status column
• **Personnel Gantt** - Red markers on allocation bars`
      }
    ],
    tips: [
      'Common certifications: Working at Heights, Confined Space, First Aid, Forklift License',
      'Set up certification types in Data Management for consistency',
      'Review compliance before mobilizing personnel to site'
    ],
    relatedArticles: ['pe-1', 'pe-4', 'rp-3']
  },

  'pe-4': {
    title: 'Medical Records & Inductions',
    category: 'Personnel Management',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `Track medical clearances and site inductions to ensure personnel are fit for work and properly inducted before mobilization.`
      },
      {
        heading: 'Medical Records',
        content: `
1. Edit the personnel record
2. Go to the **Medical** tab
3. Click **Add Medical**
4. Enter:
   • **Type** - Assessment type (e.g., "Pre-Employment", "Annual")
   • **Status** - Cleared, Restricted, Pending
   • **Assessment Date** - When examined
   • **Expiry Date** - When clearance expires
   • **Restrictions** - Any work limitations
   • **Provider** - Medical facility/doctor`
      },
      {
        heading: 'Site Inductions',
        content: `
1. Go to the **Inductions** tab
2. Click **Add Induction**
3. Enter:
   • **Site** - Project or location name
   • **Type** - General, Site-Specific, Safety, etc.
   • **Completed Date** - When inducted
   • **Expiry Date** - When re-induction needed
   • **Status** - Active, Expired`
      },
      {
        heading: 'Compliance Integration',
        content: `
Medical and induction expiries appear alongside certifications in:
• Dashboard alerts
• Personnel compliance indicators
• Gantt view markers (purple for medical)`
      }
    ],
    tips: [
      'Many sites require annual medical clearance renewals',
      'Site-specific inductions may be required for each project',
      'Keep medical records current for emergency response planning'
    ],
    relatedArticles: ['pe-1', 'pe-3', 'rp-3']
  },

  'pe-5': {
    title: 'Using the Personnel Gantt View',
    category: 'Personnel Management',
    readTime: '7 min',
    sections: [
      {
        heading: 'Overview',
        content: `The Personnel Gantt View provides a visual timeline of all personnel allocations, rotation schedules, flight dates, and compliance status. Access it by clicking "Gantt View" on the Personnel page.`
      },
      {
        heading: 'Opening the Gantt View',
        content: `
1. Navigate to **Personnel**
2. Click the **Gantt View** button (top right, next to Add Personnel)
3. The view opens as a full-screen sheet`
      },
      {
        heading: 'Understanding the Display',
        content: `
**Left Panel**
• Personnel grouped by role
• Name, role, and rotation pattern
• Compliance warning icons (visa, cert, medical)

**Timeline Area**
• Allocation bars showing project assignments
• Rotation segments:
  - **Blue** = Rostered On (at work)
  - **Gray** = R&R (rest period)
• Compliance markers on bars:
  - **Orange** = Visa expiry
  - **Red** = Certification expiry
  - **Purple** = Medical expiry`
      },
      {
        heading: 'Flight Markers (FIFO)',
        content: `
When personnel have FIFO records with flight details, plane icons appear on the timeline:

**Fly In (Green plane, tilted up)**
• Indicates arrival at site
• Positioned on the Fly In date
• Hover to see: flight number, route (e.g., PER → PHE), time

**Fly Out (Blue plane, tilted down)**
• Indicates departure from site
• Positioned on the Fly Out date
• Hover to see: flight number, route, time

Flight markers help visualize:
• When workers are traveling
• Alignment between flights and roster patterns
• Potential scheduling conflicts`
      },
      {
        heading: 'Timeline Controls',
        content: `
**View Options**
• **Day** - Detailed daily view (~3 weeks)
• **Week** - Standard view (~3 months)
• **Month** - High-level view (3 months)

**Navigation**
• **Today** - Jump to current date
• **← / →** - Move timeline backward/forward
• **Search** - Filter by personnel name or role`
      },
      {
        heading: 'Tooltips',
        content: `
Hover over any allocation bar to see:
• Personnel name and role
• Project name and status
• Allocation start and end dates
• Roster pattern and next rotation
• Compliance alerts if any`
      },
      {
        heading: 'Legend',
        content: `
The legend bar at the top shows all visual indicators:
• **Allocated** - Base project allocation bar
• **Rostered On** - Blue segments (at work)
• **R&R** - Gray segments (rest period)
• **Visa Expiry** - Orange marker
• **Cert Expiry** - Red marker
• **Medical Expiry** - Purple marker
• **Fly In** - Green plane icon
• **Fly Out** - Blue plane icon`
      }
    ],
    tips: [
      'Use Week view for day-to-day planning',
      'Month view is great for long-term resource overview',
      'Orange/red markers indicate action needed before that date',
      'Plane icons only appear when FIFO records have flight details entered',
      'Hover over plane icons to see flight number and route'
    ],
    relatedArticles: ['pe-2', 'pl-2', 'pr-3']
  },

  // ==========================================
  // PROJECT MANAGEMENT
  // ==========================================

  'pr-1': {
    title: 'Creating a New Project',
    category: 'Project Management',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `Projects are the foundation for resource planning. Create projects to allocate equipment and personnel, track budgets, and monitor progress.`
      },
      {
        heading: 'Step-by-Step Guide',
        content: `
1. Navigate to **Projects** from the main menu
2. Click **Add Project**
3. Fill in the **General** tab:
   • **Project Name** - Descriptive name
   • **Client** - Customer or client name
   • **Location** - Project site location
   • **Start Date** - Project commencement
   • **End Date** - Expected completion
   • **Status** - Planning, Active, On Hold, Completed
4. Add **Budget** information:
   • Equipment budget
   • Personnel budget
   • Contingency
5. Click **Create** to save`
      },
      {
        heading: 'Project Lifecycle',
        content: `
• **Planning** - Initial setup, budgeting
• **Active** - Work in progress
• **On Hold** - Temporarily paused
• **Completed** - Finished`
      }
    ],
    tips: [
      'Set realistic start/end dates for accurate planning',
      'Budget information enables Forecast vs Actual tracking',
      'Use the Description field to capture project scope'
    ],
    relatedArticles: ['pr-2', 'pr-3', 'pr-4']
  },

  'pr-2': {
    title: 'Project Budgeting & Forecasting',
    category: 'Project Management',
    readTime: '6 min',
    sections: [
      {
        heading: 'Overview',
        content: `Track project budgets and compare planned (forecast) costs against actual allocations. This helps identify projects running over or under budget.`
      },
      {
        heading: 'Setting Up Budgets',
        content: `
1. Edit the project
2. Go to the **Budget** tab
3. Enter budget amounts:
   • **Equipment Budget** - Expected equipment costs
   • **Personnel Budget** - Expected labor costs
   • **Contingency** - Buffer for unexpected costs
4. Set rates (used for calculations):
   • Daily equipment rate
   • Daily personnel rate`
      },
      {
        heading: 'Forecast vs Actual',
        content: `
The system calculates actual costs based on:
• **Equipment Allocations** - Days allocated × rate
• **Personnel Allocations** - Days allocated × rate

**Viewing Comparison**
• Open project details
• View the Actual vs Forecast panel
• Compare budgeted amounts to calculated actuals
• Identify variances requiring attention`
      },
      {
        heading: 'On the Planner',
        content: `
Both Equipment and Personnel Planners show a project-centric view with:
• Forecast totals from budget
• Actual totals from current allocations
• Visual comparison for quick assessment`
      }
    ],
    tips: [
      'Update rates in Data Management to match your cost structure',
      'Review Actual vs Forecast weekly during active projects',
      'Contingency should typically be 10-15% of total budget'
    ],
    relatedArticles: ['pr-1', 'pr-3', 'rp-2']
  },

  'pr-3': {
    title: 'Allocating Resources to Projects',
    category: 'Project Management',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `Allocate equipment and personnel to projects using the visual planners. Allocations define who and what is assigned to each project and when.`
      },
      {
        heading: 'Equipment Allocation',
        content: `
1. Go to **Equipment Planner**
2. Find the equipment in the left panel
3. Drag on the timeline to create an allocation
4. Set details in the popup:
   • Project selection
   • Start and end dates
   • Status
5. Save the allocation`
      },
      {
        heading: 'Personnel Allocation',
        content: `
1. Go to **Personnel Planner**
2. Find the person in the left panel
3. Drag on the timeline to create an allocation
4. Set details:
   • Project selection
   • Start and end dates
   • Role on project
5. Save the allocation`
      },
      {
        heading: 'Viewing Allocations',
        content: `
• **Planner View** - See all allocations on timeline
• **Project View** - See resources assigned to specific project
• **Personnel Gantt** - See all assignments for a person`
      }
    ],
    tips: [
      'Check for conflicts before finalizing allocations',
      'Consider rotation schedules when allocating FIFO workers',
      'Use the project filter to focus on specific projects'
    ],
    relatedArticles: ['pl-1', 'pl-2', 'pr-2']
  },

  'pr-4': {
    title: 'Project Status & Lifecycle',
    category: 'Project Management',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `Manage projects through their lifecycle using status updates. Status affects how projects appear in lists and planners.`
      },
      {
        heading: 'Status Types',
        content: `
**Planning**
• Initial project setup phase
• Budgeting and resource planning
• Not yet commenced

**Active**
• Work in progress
• Resources mobilized
• Primary operational status

**On Hold**
• Temporarily paused
• Resources may be reallocated
• Awaiting restart decision

**Completed**
• Work finished
• Final reporting phase
• Resources demobilized`
      },
      {
        heading: 'Changing Status',
        content: `
1. Go to **Projects**
2. Click the actions menu (⋮) on the project row
3. Select **Edit**
4. Change the Status field
5. Save changes`
      }
    ],
    tips: [
      'Active projects appear prominently in planners',
      'Completed projects are archived but remain searchable',
      'Use On Hold for projects with uncertain timelines'
    ],
    relatedArticles: ['pr-1', 'pr-3']
  },

  // ==========================================
  // PLANNING TOOLS
  // ==========================================

  'pl-1': {
    title: 'Equipment Planner Overview',
    category: 'Planning Tools',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `The Equipment Planner provides a visual Gantt-style interface for scheduling equipment across projects. See availability, allocations, and maintenance at a glance.`
      },
      {
        heading: 'Layout',
        content: `
**Left Panel**
• Equipment list with status indicators
• Colored dots show current status:
  - Green = Available
  - Blue = In Use
  - Orange = Maintenance
  - Red = Under Repair

**Center Panel (Gantt)**
• Timeline with allocation bars
• Maintenance periods shown as overlay
• Color-coded by project status

**Right Panel**
• Project-centric view (when project selected)
• Shows allocated equipment for selected project
• Actual vs Forecast comparison`
      },
      {
        heading: 'Key Features',
        content: `
• **Resizable bars** - Drag bar edges to adjust dates
• **Maintenance overlay** - Gray stripes show maintenance windows
• **Project filter** - Focus on specific project
• **Timeline controls** - Day/Week/Month views`
      }
    ],
    tips: [
      'Use Week view for detailed scheduling',
      'Check maintenance periods before creating allocations',
      'Double-click a bar to edit allocation details'
    ],
    relatedArticles: ['pl-2', 'pl-3', 'pl-4', 'pr-3']
  },

  'pl-2': {
    title: 'Personnel Planner Overview',
    category: 'Planning Tools',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `The Personnel Planner shows workforce scheduling across projects. Plan allocations considering skills, availability, and rotation patterns.`
      },
      {
        heading: 'Layout',
        content: `
**Left Panel**
• Personnel list grouped by role
• Shows current allocation status
• FIFO rotation indicator

**Center Panel (Gantt)**
• Allocation bars for each person
• Project-colored bars
• Timeline spans multiple months

**Right Panel**
• Project-centric view
• Personnel allocated to selected project
• Actual vs Forecast totals`
      },
      {
        heading: 'Key Features',
        content: `
• **Role grouping** - Personnel organized by job role
• **Resizable bars** - Adjust allocation dates by dragging
• **Project filter** - View specific project allocations
• **Skill matching** - Filter by required skills`
      }
    ],
    tips: [
      'Consider rotation patterns when planning allocations',
      'Check visa/certification expiries before allocating',
      'Use the Personnel Gantt View for detailed roster visualization'
    ],
    relatedArticles: ['pl-1', 'pl-3', 'pe-5', 'pr-3']
  },

  'pl-3': {
    title: 'Drag & Drop Allocations',
    category: 'Planning Tools',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `Create and modify allocations visually by dragging on the timeline. This intuitive interface makes scheduling fast and efficient.`
      },
      {
        heading: 'Creating Allocations',
        content: `
1. Open the Equipment or Personnel Planner
2. Find the resource in the left panel
3. Click and drag on the timeline row
4. Release to create an allocation
5. Fill in the popup form:
   • Select project
   • Confirm dates
   • Set status
6. Click Save`
      },
      {
        heading: 'Modifying Allocations',
        content: `
**Move an allocation**
• Click and drag the bar to a new position

**Resize an allocation**
• Hover over the left or right edge
• Drag to adjust start or end date

**Edit details**
• Double-click the bar
• Update fields in the popup
• Save changes`
      },
      {
        heading: 'Deleting Allocations',
        content: `
1. Click on the allocation bar
2. Click the Delete option
3. Confirm the deletion`
      }
    ],
    tips: [
      'Hold Shift while dragging to constrain movement',
      'Watch for conflict warnings when overlapping allocations',
      'Undo is not available - delete and recreate if needed'
    ],
    relatedArticles: ['pl-1', 'pl-2', 'pr-3']
  },

  'pl-4': {
    title: 'Timeline Views (Day/Week/Month)',
    category: 'Planning Tools',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `Switch between timeline scales to see different levels of detail. Choose the view that best suits your planning needs.`
      },
      {
        heading: 'View Options',
        content: `
**Day View**
• Shows individual days
• Best for: Detailed short-term scheduling
• Span: ~3 weeks visible

**Week View** (Default)
• Shows weeks (W1, W2, etc.)
• Best for: Standard planning
• Span: ~3 months visible

**Month View**
• Shows months
• Best for: Long-term overview
• Span: 3+ months visible`
      },
      {
        heading: 'Switching Views',
        content: `
1. Find the Day/Week/Month buttons in the toolbar
2. Click your preferred view
3. Timeline updates immediately

**Navigation**
• Use ← / → arrows to move timeline
• Click "Today" to center on current date`
      }
    ],
    tips: [
      'Use Day view when finalizing near-term schedules',
      'Month view is great for portfolio overview',
      'The view preference is remembered during your session'
    ],
    relatedArticles: ['pl-1', 'pl-2', 'pe-5']
  },

  // ==========================================
  // REPORTS & ANALYTICS
  // ==========================================

  'rp-1': {
    title: 'Dashboard Metrics Explained',
    category: 'Reports & Analytics',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `The Dashboard displays key performance indicators (KPIs) and metrics. Understanding these helps you make informed decisions.`
      },
      {
        heading: 'Summary Metrics',
        content: `
**Equipment**
• Total count of equipment
• Breakdown by status (Available, In Use, etc.)

**Personnel**
• Total workforce count
• Currently allocated vs available

**Projects**
• Active project count
• Projects by status

**Alerts**
• Number of active alerts
• Breakdown by priority`
      },
      {
        heading: 'Utilisation Metrics',
        content: `
**Equipment Utilisation**
• Percentage of fleet currently allocated
• Identifies idle assets

**Personnel Utilisation**
• Percentage of workforce allocated
• Identifies available capacity`
      }
    ],
    tips: [
      'Low utilisation may indicate planning opportunities',
      'High alert counts need immediate attention',
      'Click metrics to navigate to detailed views'
    ],
    relatedArticles: ['gs-2', 'rp-2', 'rp-3']
  },

  'rp-2': {
    title: 'Utilisation Reports',
    category: 'Reports & Analytics',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `Track how effectively your resources are being used. Utilisation reports help optimize allocation and identify improvement opportunities.`
      },
      {
        heading: 'Equipment Utilisation',
        content: `
Measures the percentage of equipment allocated to projects:
• **High utilisation (>80%)** - Fleet well-utilized
• **Medium (50-80%)** - Some spare capacity
• **Low (<50%)** - Significant idle assets

**Factors to consider**
• Maintenance windows reduce available time
• Seasonality affects demand
• Equipment condition limits deployability`
      },
      {
        heading: 'Personnel Utilisation',
        content: `
Measures workforce allocation rates:
• **Allocated** - Currently assigned to projects
• **Available** - Not currently assigned
• **On R&R** - In rotation rest period

**Factors to consider**
• Rotation patterns affect availability
• Training and leave reduce capacity
• Skill requirements limit assignments`
      }
    ],
    tips: [
      'Review utilisation weekly to optimize scheduling',
      'Compare utilisation across equipment types',
      'Factor in R&R when assessing personnel availability'
    ],
    relatedArticles: ['rp-1', 'pr-2', 'gs-2']
  },

  'rp-3': {
    title: 'Compliance Alerts & Warnings',
    category: 'Reports & Analytics',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `The system monitors expiry dates and generates alerts to help maintain compliance. Understanding and acting on alerts is critical for operations.`
      },
      {
        heading: 'Alert Types',
        content: `
**Equipment Alerts**
• Maintenance due/overdue
• Certification expiring/expired
• Inspection required

**Personnel Alerts**
• Visa expiring/expired
• Certification expiring/expired
• Medical clearance expiring
• Induction expiring`
      },
      {
        heading: 'Priority Levels',
        content: `
**High Priority (Red)**
• Expired items
• Overdue maintenance
• Immediate action required

**Medium Priority (Yellow)**
• Expiring within 30 days
• Action needed soon

**Low Priority**
• Expiring within 90 days
• Plan ahead`
      },
      {
        heading: 'Viewing Alerts',
        content: `
• **Dashboard** - All alerts in one view
• **Notifications** - Bell icon in top nav
• **List Views** - Compliance columns on Equipment/Personnel
• **Gantt Views** - Markers on allocation bars`
      }
    ],
    tips: [
      'Address high-priority alerts immediately',
      'Schedule renewals before expiry dates',
      'Use alerts to prevent non-compliant deployments'
    ],
    relatedArticles: ['eq-4', 'pe-3', 'pe-4', 'gs-2']
  },

  // ==========================================
  // ADMINISTRATION
  // ==========================================

  'ad-1': {
    title: 'Managing Users',
    category: 'Administration',
    readTime: '4 min',
    sections: [
      {
        heading: 'Overview',
        content: `User management allows administrators to create accounts, assign roles, and control access to EQUIPR Horizon.`
      },
      {
        heading: 'Adding Users',
        content: `
1. Navigate to **Users** from the menu
2. Click **Add User**
3. Enter user details:
   • **Username** - Login identifier
   • **Password** - Initial password
   • **Role** - Admin, Manager, or Viewer
4. Click **Create**

The user can now log in with the Client ID for your organization.`
      },
      {
        heading: 'Editing Users',
        content: `
1. Find the user in the list
2. Click the actions menu (⋮)
3. Select **Edit**
4. Update role or reset password
5. Save changes`
      },
      {
        heading: 'Deactivating Users',
        content: `
Users cannot be deleted but can be deactivated:
1. Edit the user
2. Set status to Inactive
3. Save changes

Inactive users cannot log in but their activity history is preserved.`
      }
    ],
    tips: [
      'Use Viewer role for read-only access',
      'Managers can do everything except user management',
      'Document user role assignments for audit purposes'
    ],
    relatedArticles: ['gs-4', 'ad-3']
  },

  'ad-2': {
    title: 'Data Management (Categories)',
    category: 'Administration',
    readTime: '5 min',
    sections: [
      {
        heading: 'Overview',
        content: `Data Management lets you configure the categories and lookup values used throughout EQUIPR Horizon. Set up these before adding operational data.`
      },
      {
        heading: 'Category Types',
        content: `
**Equipment Categories**
• Equipment Type - Crane, Forklift, etc.
• Equipment Status - Available, In Use, etc.

**Personnel Categories**
• Personnel Role - Project Manager, Engineer, etc.
• Rotation - 2:2, 3:1, etc.

**Other**
• Certification types
• Medical assessment types
• Induction types`
      },
      {
        heading: 'Managing Categories',
        content: `
1. Go to **Data Management**
2. Select category type from dropdown
3. View existing items
4. Click **Add Category** to create new
5. Enter name and description
6. Save

**Editing/Deleting**
• Click actions menu on any row
• Edit to modify, Delete to remove
• Items in use cannot be deleted`
      }
    ],
    tips: [
      'Set up categories before importing data',
      'Use consistent naming conventions',
      'Deactivate unused categories rather than deleting'
    ],
    relatedArticles: ['eq-2', 'pe-1', 'ad-1']
  },

  'ad-3': {
    title: 'System Settings',
    category: 'Administration',
    readTime: '3 min',
    sections: [
      {
        heading: 'Overview',
        content: `System settings control global configurations for your EQUIPR Horizon instance.`
      },
      {
        heading: 'Available Settings',
        content: `
**Display Settings**
• Date format (DD/MM/YYYY)
• Default timezone
• Default planner view

**Alert Settings**
• Days before expiry to alert
• Email notification preferences
• Alert priority thresholds

**Integration Settings**
• API configurations (future)
• External system connections (future)`
      },
      {
        heading: 'Accessing Settings',
        content: `
Settings are currently managed at the system level. Contact your administrator or support for configuration changes.

Future releases will include a Settings page for admin users.`
      }
    ],
    tips: [
      'Date format is standardized to DD/MM/YYYY',
      'Contact support for custom configuration needs',
      'Settings changes may require page refresh'
    ],
    relatedArticles: ['ad-1', 'ad-2', 'gs-4']
  }
};

// Helper function to get content by article ID
export const getArticleContent = (articleId) => {
  return HELP_CONTENT[articleId] || null;
};

// Helper function to search articles
export const searchArticles = (query) => {
  const results = [];
  const lowerQuery = query.toLowerCase();
  
  Object.entries(HELP_CONTENT).forEach(([id, article]) => {
    const matchScore = 
      (article.title.toLowerCase().includes(lowerQuery) ? 10 : 0) +
      (article.category.toLowerCase().includes(lowerQuery) ? 5 : 0) +
      (article.sections.some(s => s.content.toLowerCase().includes(lowerQuery)) ? 3 : 0);
    
    if (matchScore > 0) {
      results.push({ id, ...article, matchScore });
    }
  });
  
  return results.sort((a, b) => b.matchScore - a.matchScore);
};
